import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Check, Pause, Star, Eye, Loader2, Pencil, X, Upload, Image, Film, Mic, Download, Table, Filter, Compass } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast, Toaster } from "sonner";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import InstagramCard from "../components/InstagramCard";

const API = "/api";

/* Separate text from <div class="visual"> HTML blocks */
function parseInsightContent(text) {
  if (!text) return { textContent: "", visualHtml: "" };
  const visualRegex = /<div\s+class="visual"[\s\S]*?<\/div>\s*$/i;
  // Try to find the visual block - it could be wrapped in code fences
  let cleaned = text;
  // Remove markdown code fences around HTML
  cleaned = cleaned.replace(/```html\s*\n?([\s\S]*?)```/gi, "$1");
  cleaned = cleaned.replace(/```\s*\n?([\s\S]*?)```/gi, "$1");
  
  const match = cleaned.match(/<div\s+class=["']visual["'][\s\S]*$/i);
  if (match) {
    const visualHtml = match[0];
    const textContent = cleaned.slice(0, match.index).trim();
    return { textContent, visualHtml };
  }
  return { textContent: cleaned, visualHtml: "" };
}

/* Extract a clean title from Claude's markdown (first H1/H2 heading) */
function extractTitleFromMarkdown(text) {
  if (!text) return "";
  // Remove code fences
  const cleaned = text.replace(/```[\s\S]*?```/g, "");
  // Look for first # or ## heading
  const headingMatch = cleaned.match(/^#{1,2}\s+(.+)$/m);
  if (headingMatch) return headingMatch[1].trim();
  // Fallback: first bold text
  const boldMatch = cleaned.match(/\*\*(.+?)\*\*/);
  if (boldMatch) return boldMatch[1].trim();
  // Fallback: first sentence
  const firstSentence = cleaned.trim().split(/[.!?\n]/)[0];
  return firstSentence?.slice(0, 80) || "";
}

const ACCEPTED_TYPES = {
  "image/jpeg": "image",
  "image/png": "image",
  "image/webp": "image",
  "video/mp4": "video",
  "video/quicktime": "video",
  "audio/mpeg": "audio",
  "audio/wav": "audio",
  "audio/mp3": "audio",
};

function AdminLogin({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(res.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
      <Toaster position="top-center" richColors />
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex items-baseline justify-center gap-0 text-2xl font-medium tracking-tight select-none mb-2">
            <span className="text-[#1F2937]">iam</span>
            <span className="text-[#C17F24]">because</span>
          </div>
          <p className="text-sm text-[#4B5563]">Admin Panel</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#E5E7EB] p-8 space-y-5 shadow-sm">
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
              Username
            </label>
            <Input
              data-testid="admin-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-11"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#4B5563]">
              Password
            </label>
            <Input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-11"
              required
            />
          </div>
          {error && <p data-testid="admin-login-error" className="text-sm text-red-500">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="btn-amber w-full !py-3"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const variants = {
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    held: "bg-gray-100 text-gray-600 border-gray-200",
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${variants[status] || variants.pending}`}>
      {status}
    </span>
  );
}

function ModerationBadge({ score, flags, summary }) {
  const [showTooltip, setShowTooltip] = useState(false);
  
  let colorClass, label;
  if (score >= 90) {
    colorClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
    label = "Safe";
  } else if (score >= 70) {
    colorClass = "bg-blue-50 text-blue-700 border-blue-200";
    label = "Review";
  } else if (score >= 40) {
    colorClass = "bg-amber-50 text-amber-700 border-amber-200";
    label = "Caution";
  } else {
    colorClass = "bg-red-50 text-red-700 border-red-200";
    label = "Flagged";
  }
  
  return (
    <span
      data-testid="moderation-badge"
      className={`relative inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border cursor-help ${colorClass}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      AI: {score} &middot; {label}
      {showTooltip && summary && (
        <span className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-[#1F2937] text-white text-xs rounded-lg shadow-lg w-64 z-50 font-normal leading-relaxed">
          {summary}
          {flags && flags.length > 0 && (
            <span className="block mt-1 text-red-300">Flags: {flags.join(", ")}</span>
          )}
        </span>
      )}
    </span>
  );
}

function EditModal({ story, token, onClose, onSaved }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: story.real_name || story.name || "",
    role: story.role || "",
    why: story.why || "",
    city: story.city || "",
    country: story.country || "",
    email: story.email || "",
    media_url: story.media_url || "",
    media_type: story.media_type || "",
    anonymous: story.anonymous || false,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Max 50MB.");
      return;
    }

    const mediaType = ACCEPTED_TYPES[file.type];
    if (!mediaType) {
      toast.error("Unsupported file type.");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const resourceType = mediaType === "audio" ? "raw" : mediaType;
      const sigRes = await axios.get(`${API}/cloudinary/signature`, {
        params: { resource_type: resourceType, folder: "iambecause" },
      });
      const sig = sigRes.data;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);

      const uploadRes = await axios.post(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
        formData,
        {
          onUploadProgress: (p) => {
            if (p.total) setUploadProgress(Math.round((p.loaded / p.total) * 100));
          },
        }
      );

      setForm({ ...form, media_url: uploadRes.data.secure_url, media_type: mediaType });
      toast.success("Media uploaded!");
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const removeMedia = () => {
    setForm({ ...form, media_url: "", media_type: "" });
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.role.trim() || !form.why.trim()) {
      toast.error("Name, role, and why are required.");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/stories/${story.id}`, {
        name: form.name,
        role: form.role,
        why: form.why,
        city: form.city || null,
        country: form.country || null,
        email: form.email || null,
        media_url: form.media_url || null,
        media_type: form.media_type || null,
        anonymous: form.anonymous || false,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Story updated!");
      onSaved();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        localStorage.removeItem("admin_token");
        window.location.reload();
        return;
      }
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        data-testid="edit-story-modal"
        className="bg-white rounded-2xl border border-[#E5E7EB] w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
          <h2 className="text-lg font-sans font-semibold text-[#1F2937]">Edit Story</h2>
          <button data-testid="close-edit-modal" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#FAF9F6]">
            <X size={20} className="text-[#4B5563]" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Name *</Label>
              <Input
                name="name"
                data-testid="edit-name"
                value={form.name}
                onChange={handleChange}
                className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Role *</Label>
              <Input
                name="role"
                data-testid="edit-role"
                value={form.role}
                onChange={handleChange}
                className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Why *</Label>
            <Textarea
              name="why"
              data-testid="edit-why"
              value={form.why}
              onChange={handleChange}
              rows={6}
              className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] font-serif leading-relaxed"
            />
          </div>

          {/* Media */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Media</Label>
            {form.media_url ? (
              <div className="relative rounded-xl border border-[#E5E7EB] overflow-hidden">
                {form.media_type === "image" && (
                  <img src={form.media_url} alt="" className="w-full max-h-48 object-cover" />
                )}
                {form.media_type === "video" && (
                  <video src={form.media_url} controls className="w-full max-h-48" />
                )}
                {form.media_type === "audio" && (
                  <div className="p-4 bg-[#FAF9F6] flex items-center gap-3">
                    <Mic size={20} className="text-[#C17F24]" />
                    <audio src={form.media_url} controls className="flex-1" />
                  </div>
                )}
                {!form.media_type && form.media_url && (
                  <div className="p-4 text-sm text-[#4B5563] break-all">{form.media_url}</div>
                )}
                <div className="absolute top-2 right-2 flex gap-1.5">
                  <button
                    type="button"
                    data-testid="replace-media-btn"
                    onClick={() => fileRef.current?.click()}
                    className="w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"
                  >
                    <Upload size={14} />
                  </button>
                  <button
                    type="button"
                    data-testid="remove-media-edit-btn"
                    onClick={removeMedia}
                    className="w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                data-testid="upload-media-edit-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full rounded-xl border-2 border-dashed border-[#C17F24]/30 bg-[#C17F24]/5 hover:border-[#C17F24]/60 hover:bg-[#C17F24]/10 p-8 transition-colors text-center cursor-pointer"
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 size={24} className="animate-spin text-[#C17F24]" />
                    <span className="text-sm font-semibold text-[#C17F24]">Uploading... {uploadProgress}%</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex items-center gap-3 text-[#C17F24]">
                      <Image size={22} /><Film size={22} /><Mic size={22} />
                    </div>
                    <span className="text-sm font-semibold text-[#C17F24]">Click to upload photo, video, or voice note</span>
                    <span className="text-xs text-[#4B5563]/60">JPG, PNG, MP4, MOV, MP3, WAV up to 50MB</span>
                  </div>
                )}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.mp3,.wav"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">City</Label>
              <Input
                name="city"
                data-testid="edit-city"
                value={form.city}
                onChange={handleChange}
                className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Country</Label>
              <Input
                name="country"
                data-testid="edit-country"
                value={form.country}
                onChange={handleChange}
                className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">
              Email <span className="normal-case tracking-normal font-normal text-[#4B5563]/50">(private)</span>
            </Label>
            <Input
              name="email"
              type="email"
              data-testid="edit-email"
              value={form.email}
              onChange={handleChange}
              className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10"
            />
            {story?.whatsapp && (
              <p data-testid="story-whatsapp" className="text-xs font-sans mt-1" style={{ color: '#25D366' }}>
                WhatsApp: {story.whatsapp}
              </p>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E5E7EB]">
          <button data-testid="cancel-edit-btn" onClick={onClose} className="btn-ghost !py-2.5 !px-6 text-sm">
            Cancel
          </button>
          <button
            data-testid="save-edit-btn"
            onClick={handleSave}
            disabled={saving}
            className="btn-amber !py-2.5 !px-6 text-sm"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ token, onClose, onCreated }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    name: "", role: "", why: "", city: "", country: "", email: "",
    media_url: "", media_type: "", status: "approved", featured: false,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error("File too large. Max 50MB."); return; }
    const mediaType = ACCEPTED_TYPES[file.type];
    if (!mediaType) { toast.error("Unsupported file type."); return; }
    setUploading(true); setUploadProgress(0);
    try {
      const resourceType = mediaType === "audio" ? "raw" : mediaType;
      const sigRes = await axios.get(`${API}/cloudinary/signature`, { params: { resource_type: resourceType, folder: "iambecause" } });
      const sig = sigRes.data;
      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sig.api_key);
      formData.append("timestamp", sig.timestamp);
      formData.append("signature", sig.signature);
      formData.append("folder", sig.folder);
      const uploadRes = await axios.post(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`, formData, {
        onUploadProgress: (p) => { if (p.total) setUploadProgress(Math.round((p.loaded / p.total) * 100)); },
      });
      setForm((f) => ({ ...f, media_url: uploadRes.data.secure_url, media_type: mediaType }));
      toast.success("Media uploaded!");
    } catch { toast.error("Upload failed."); }
    finally { setUploading(false); setUploadProgress(0); }
  };

  const removeMedia = () => { setForm({ ...form, media_url: "", media_type: "" }); if (fileRef.current) fileRef.current.value = ""; };

  const handleSave = async () => {
    if (!form.name.trim() || !form.role.trim() || !form.why.trim()) { toast.error("Name, role, and why are required."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/stories`, {
        name: form.name, role: form.role, why: form.why,
        city: form.city || null, country: form.country || null, email: form.email || null,
        media_url: form.media_url || null, media_type: form.media_type || null,
        status: form.status, featured: form.featured,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Story created!");
      onCreated();
    } catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired."); localStorage.removeItem("admin_token"); window.location.reload(); return; }
      toast.error("Failed to create story.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div data-testid="create-story-modal" className="bg-white rounded-2xl border border-[#E5E7EB] w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[#E5E7EB]">
          <h2 className="text-lg font-sans font-semibold text-[#1F2937]">Create New Story</h2>
          <button data-testid="close-create-modal" onClick={onClose} className="p-1.5 rounded-full hover:bg-[#FAF9F6]"><X size={20} className="text-[#4B5563]" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Name *</Label>
              <Input name="name" data-testid="create-name" value={form.name} onChange={handleChange} className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Role *</Label>
              <Input name="role" data-testid="create-role" value={form.role} onChange={handleChange} className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Why *</Label>
            <Textarea name="why" data-testid="create-why" value={form.why} onChange={handleChange} rows={6} className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] font-serif leading-relaxed" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Media</Label>
            {form.media_url ? (
              <div className="relative rounded-xl border border-[#E5E7EB] overflow-hidden">
                {form.media_type === "image" && <img src={form.media_url} alt="" className="w-full max-h-48 object-cover" />}
                {form.media_type === "video" && <video src={form.media_url} controls className="w-full max-h-48" />}
                {form.media_type === "audio" && <div className="p-4 bg-[#FAF9F6] flex items-center gap-3"><Mic size={20} className="text-[#C17F24]" /><audio src={form.media_url} controls className="flex-1" /></div>}
                <div className="absolute top-2 right-2 flex gap-1.5">
                  <button type="button" onClick={() => fileRef.current?.click()} className="w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"><Upload size={14} /></button>
                  <button type="button" onClick={removeMedia} className="w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white"><X size={14} /></button>
                </div>
              </div>
            ) : (
              <button type="button" data-testid="create-upload-media" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="w-full rounded-xl border-2 border-dashed border-[#C17F24]/30 bg-[#C17F24]/5 hover:border-[#C17F24]/60 hover:bg-[#C17F24]/10 p-8 transition-colors text-center cursor-pointer">
                {uploading ? (
                  <div className="flex flex-col items-center gap-2"><Loader2 size={24} className="animate-spin text-[#C17F24]" /><span className="text-sm font-semibold text-[#C17F24]">Uploading... {uploadProgress}%</span></div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex items-center gap-3 text-[#C17F24]"><Image size={22} /><Film size={22} /><Mic size={22} /></div>
                    <span className="text-sm font-semibold text-[#C17F24]">Click to upload photo, video, or voice note</span>
                    <span className="text-xs text-[#4B5563]/60">JPG, PNG, MP4, MOV, MP3, WAV up to 50MB</span>
                  </div>
                )}
              </button>
            )}
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.mp3,.wav" onChange={handleFileUpload} className="hidden" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">City</Label>
              <Input name="city" data-testid="create-city" value={form.city} onChange={handleChange} className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Country</Label>
              <Input name="country" data-testid="create-country" value={form.country} onChange={handleChange} className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Email <span className="normal-case tracking-normal font-normal text-[#4B5563]/50">(private)</span></Label>
            <Input name="email" type="email" data-testid="create-email" value={form.email} onChange={handleChange} className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-10" />
          </div>
          <div className="flex items-center gap-6 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Status</Label>
              <select name="status" data-testid="create-status" value={form.status} onChange={handleChange}
                className="rounded-xl border border-[#E5E7EB] px-3 h-10 text-sm focus:ring-2 focus:ring-[#C17F24] focus:outline-none">
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="held">Held</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer pt-5">
              <input type="checkbox" name="featured" checked={form.featured} onChange={handleChange} className="accent-[#C17F24] w-4 h-4" />
              <span className="text-sm font-sans font-medium text-[#4B5563]">Featured story</span>
            </label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E5E7EB]">
          <button data-testid="cancel-create-btn" onClick={onClose} className="btn-ghost !py-2.5 !px-6 text-sm">Cancel</button>
          <button data-testid="save-create-btn" onClick={handleSave} disabled={saving} className="btn-amber !py-2.5 !px-6 text-sm">
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Create Story"}
          </button>
        </div>
      </div>
    </div>
  );
}


function BarChart({ data, labelKey, valueKey, maxBars = 10, color = "#C17F24" }) {
  const items = data.slice(0, maxBars);
  const max = Math.max(...items.map(d => d[valueKey]), 1);
  return (
    <div className="space-y-2">
      {items.map((d, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-xs font-sans text-[#4B5563] w-32 truncate text-right">{d[labelKey]}</span>
          <div className="flex-1 h-6 bg-[#FAF9F6] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(d[valueKey] / max) * 100}%`, backgroundColor: color }} />
          </div>
          <span className="text-xs font-sans font-semibold text-[#1F2937] w-8">{d[valueKey]}</span>
        </div>
      ))}
    </div>
  );
}

function SceneRateCard({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/scene-rate`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data)).catch(() => {});
  }, [token]);
  return (
    <div data-testid="scene-rate-card" className="rounded-xl p-4" style={{ background: '#16130F' }}>
      <p className="text-2xl font-sans font-semibold" style={{ color: '#E91E8C' }}>
        {data && data.scene_rate !== null ? `${data.scene_rate}%` : '—'}
      </p>
      <p className="text-xs uppercase tracking-[0.1em] font-sans mt-1" style={{ color: 'rgba(254,252,247,0.6)' }}>
        Named-Door Rate ★
      </p>
      {data && data.scored > 0 && <p className="text-[10px] font-sans mt-1" style={{ color: 'rgba(254,252,247,0.35)' }}>{data.with_scene}/{data.scored} scored whys name a scene</p>}
    </div>
  );
}

function InsightsPanel({ token }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  // Custom analysis
  const [customPrompt, setCustomPrompt] = useState("");
  const [customAnswer, setCustomAnswer] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);
  const [promptHistory, setPromptHistory] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/admin/insights`, { headers: { Authorization: `Bearer ${token}` } });
        setInsights(res.data);
      } catch { toast.error("Failed to load insights"); }
      finally { setLoading(false); }
    };
    fetch();
  }, [token]);

  const analyzeAll = async (force = false) => {
    setAnalyzing(true);
    try {
      const url = force ? `${API}/admin/analyze-all?force=true` : `${API}/admin/analyze-all`;
      const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Analyzed ${res.data.analyzed} of ${res.data.total} stories`);
      const res2 = await axios.get(`${API}/admin/insights`, { headers: { Authorization: `Bearer ${token}` } });
      setInsights(res2.data);
    } catch { toast.error("Analysis failed"); }
    finally { setAnalyzing(false); }
  };

  const runCustomAnalysis = async () => {
    if (!customPrompt.trim()) return;
    setCustomLoading(true);
    setCustomAnswer(null);
    try {
      const res = await axios.post(`${API}/admin/analyze-custom`, { prompt: customPrompt }, { headers: { Authorization: `Bearer ${token}` } });
      const autoTitle = extractTitleFromMarkdown(res.data.answer);
      const result = { text: res.data.answer, count: res.data.stories_analyzed, prompt: customPrompt, title: autoTitle || customPrompt };
      setCustomAnswer(result);
      setPromptHistory(prev => [{ prompt: customPrompt, answer: res.data.answer, count: res.data.stories_analyzed }, ...prev.slice(0, 9)]);
    } catch { toast.error("Analysis failed"); }
    finally { setCustomLoading(false); }
  };

  const [publishTitle, setPublishTitle] = useState("");
  const [showPublishModal, setShowPublishModal] = useState(false);

  const openPublishModal = () => {
    if (!customAnswer) return;
    setPublishTitle(customAnswer.title || extractTitleFromMarkdown(customAnswer.text) || "Insight");
    setShowPublishModal(true);
  };

  const publishInsight = async () => {
    if (!customAnswer) return;
    try {
      await axios.post(`${API}/admin/insights/publish`, {
        prompt: customAnswer.prompt,
        title: publishTitle.trim() || customAnswer.title,
        answer: customAnswer.text,
        stories_analyzed: customAnswer.count,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Insight published to site!");
      setShowPublishModal(false);
      fetchPublished();
    } catch { toast.error("Failed to publish"); }
  };

  const [publishedInsights, setPublishedInsights] = useState([]);
  const fetchPublished = async () => {
    try {
      const res = await axios.get(`${API}/admin/insights/published`, { headers: { Authorization: `Bearer ${token}` } });
      setPublishedInsights(res.data);
    } catch {}
  };
  useEffect(() => { fetchPublished(); }, [token]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#C17F24]" /></div>;
  if (!insights) return null;

  const untagged = insights.approved - insights.themes.reduce((a, t) => a + t.count, 0) / (insights.themes.length || 1);
  const hasUntagged = insights.themes.length === 0 || insights.approved > 0;

  return (
    <div data-testid="insights-panel" className="space-y-8">
      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {[
          { label: "Total Stories", value: insights.total },
          { label: "Approved", value: insights.approved },
          { label: "Pending", value: insights.status.pending },
          { label: "With Media", value: insights.media.image + insights.media.video + insights.media.audio },
          { label: "Anonymous", value: insights.anonymous },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <p className="text-2xl font-sans font-semibold text-[#1F2937]">{s.value}</p>
            <p className="text-xs uppercase tracking-[0.1em] font-sans text-[#4B5563] mt-1">{s.label}</p>
          </div>
        ))}
        <SceneRateCard token={token} />
      </div>

      {/* Analyze buttons */}
      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="analyze-new-btn" onClick={() => analyzeAll(false)} disabled={analyzing}
          className="btn-amber !py-2 !px-5 text-sm">
          {analyzing ? <><Loader2 size={15} className="animate-spin" /> Analyzing...</> : "Analyze New Stories"}
        </button>
        <button data-testid="reanalyze-all-btn" onClick={() => analyzeAll(true)} disabled={analyzing}
          className="btn-ghost !py-2 !px-5 text-sm">
          Re-analyze All
        </button>
        {insights.themes.length > 0 && (
          <span className="text-xs text-[#4B5563]">{insights.themes.length} themes detected across stories</span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Motivation Themes */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">Why People Do What They Do</h3>
          {insights.themes.length === 0 ? (
            <p className="text-sm text-[#4B5563] py-8 text-center">Click "Analyze Stories with AI" to discover motivation themes.</p>
          ) : (
            <BarChart data={insights.themes} labelKey="label" valueKey="count" />
          )}
        </div>

        {/* Top Words */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">Most Used Words</h3>
          <div className="flex flex-wrap gap-2">
            {insights.top_words.slice(0, 25).map((w, i) => {
              const size = w.count > 5 ? "text-lg" : w.count > 3 ? "text-base" : "text-sm";
              const opacity = Math.max(0.4, Math.min(1, w.count / 8));
              return (
                <span key={w.word} className={`font-serif ${size} text-[#C17F24] px-2 py-0.5 rounded-lg bg-[#C17F24]/5 border border-[#C17F24]/10`}
                  style={{ opacity }}>
                  {w.word}
                </span>
              );
            })}
          </div>
        </div>

        {/* Countries */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">Where They Come From</h3>
          <BarChart data={insights.countries} labelKey="country" valueKey="count" color="#4B5563" />
        </div>

        {/* Cities */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">Top Cities</h3>
          <BarChart data={insights.cities} labelKey="city" valueKey="count" color="#C17F24" />
        </div>

        {/* Media Types */}
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">Media Types</h3>
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(insights.media).map(([type, count]) => (
              <div key={type} className="text-center p-3 rounded-xl bg-[#FAF9F6]">
                <p className="text-xl font-sans font-semibold text-[#1F2937]">{count}</p>
                <p className="text-xs uppercase tracking-[0.1em] text-[#4B5563] capitalize">{type}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Submissions Timeline */}
        {insights.timeline.length > 0 && (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
            <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">Submission Timeline</h3>
            <div className="flex items-end gap-1 h-32">
              {insights.timeline.map((d, i) => {
                const max = Math.max(...insights.timeline.map(t => t.count), 1);
                const h = (d.count / max) * 100;
                return (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end group" title={`${d.date}: ${d.count} submissions`}>
                    <span className="text-[10px] font-sans text-[#4B5563] opacity-0 group-hover:opacity-100 mb-1">{d.count}</span>
                    <div className="w-full bg-[#C17F24] rounded-t transition-all hover:bg-[#A66C1E]" style={{ height: `${Math.max(h, 6)}%`, minHeight: '3px' }} />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-[#4B5563]">
              <span>{insights.timeline[0]?.date}</span>
              <span>{insights.timeline[insights.timeline.length - 1]?.date}</span>
            </div>
          </div>
        )}
      </div>

      {/* Custom AI Analysis */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 mt-8">
        <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-2">Ask Claude anything about your stories</h3>
        <p className="text-xs text-[#4B5563] mb-4">Claude Sonnet 4.6 will analyze all approved stories and answer your question.</p>
        
        {/* Suggested prompts */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            "What emotions appear most across all stories?",
            "Which stories mention a parent or family member?",
            "What patterns differ between cities?",
            "Write a short blog post about the most common theme",
            "What makes these stories unique compared to typical career narratives?",
          ].map(p => (
            <button key={p} onClick={() => setCustomPrompt(p)}
              className="text-xs px-3 py-1.5 rounded-full border border-[#E5E7EB] text-[#4B5563] hover:border-[#C17F24]/40 hover:text-[#C17F24] transition-colors">
              {p}
            </button>
          ))}
        </div>

        <div className="flex gap-3">
          <input
            data-testid="custom-prompt-input"
            value={customPrompt}
            onChange={e => setCustomPrompt(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && customPrompt.trim() && !customLoading) {
                e.preventDefault();
                runCustomAnalysis();
              }
            }}
            placeholder="Ask anything about your stories..."
            className="flex-1 rounded-xl border border-[#E5E7EB] px-4 h-11 text-sm focus:ring-2 focus:ring-[#C17F24] focus:outline-none"
          />
          <button
            data-testid="run-custom-analysis"
            onClick={runCustomAnalysis}
            disabled={customLoading || !customPrompt.trim()}
            className="btn-amber !py-2.5 !px-6 text-sm whitespace-nowrap"
          >
            {customLoading ? <Loader2 size={16} className="animate-spin" /> : "Ask Claude"}
          </button>
        </div>

        {/* Answer */}
        {customAnswer && (
          <div data-testid="custom-analysis-answer" className="mt-6 rounded-xl border border-[#E5E7EB] overflow-hidden">
            {/* Answer header */}
            <div className="flex items-center justify-between px-5 py-3 bg-[#FAF9F6] border-b border-[#E5E7EB]">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-[#C17F24]/10 flex items-center justify-center">
                  <span className="text-xs font-bold text-[#C17F24]">AI</span>
                </div>
                <span className="text-xs font-sans font-semibold text-[#4B5563]">Claude Sonnet 4.6 &middot; {customAnswer.count} stories</span>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="publish-insight-btn"
                  onClick={openPublishModal}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold bg-[#C17F24] text-white hover:bg-[#A66C1E] transition-colors"
                >
                  Publish to site
                </button>
              </div>
            </div>
            {/* Answer body */}
            {(() => {
              const { textContent, visualHtml } = parseInsightContent(customAnswer.text);
              return (
                <div className="max-h-[600px] overflow-y-auto">
                  {textContent && (
                    <div className="p-5 insight-markdown">
                      <ReactMarkdown
                        components={{
                          p: ({ children }) => <p className="font-serif text-base leading-relaxed text-[#1F2937] mb-4">{children}</p>,
                          strong: ({ children }) => <strong className="font-sans font-semibold text-[#1F2937]">{children}</strong>,
                          em: ({ children }) => <em className="text-[#C17F24] not-italic font-serif">{children}</em>,
                          h1: ({ children }) => <h2 className="text-xl font-sans font-semibold text-[#1F2937] mt-6 mb-3">{children}</h2>,
                          h2: ({ children }) => <h3 className="text-lg font-sans font-semibold text-[#C17F24] mt-6 mb-3">{children}</h3>,
                          h3: ({ children }) => <h4 className="text-base font-sans font-semibold text-[#1F2937] mt-4 mb-2">{children}</h4>,
                          ul: ({ children }) => <ul className="space-y-2 my-4 ml-1">{children}</ul>,
                          li: ({ children }) => <li className="flex gap-3 text-base leading-relaxed"><span className="text-[#C17F24] mt-1.5 flex-shrink-0">&#x2022;</span><span className="font-serif text-[#4B5563]">{children}</span></li>,
                          blockquote: ({ children }) => <blockquote className="border-l-3 border-[#C17F24] pl-5 my-6 bg-[#FAF9F6] py-4 pr-5 rounded-r-xl">{children}</blockquote>,
                          hr: () => <div className="my-6 flex items-center gap-3"><div className="flex-1 h-px bg-[#E5E7EB]" /><div className="w-1.5 h-1.5 rounded-full bg-[#C17F24]" /><div className="flex-1 h-px bg-[#E5E7EB]" /></div>,
                        }}
                      >
                        {textContent}
                      </ReactMarkdown>
                    </div>
                  )}
                  {visualHtml && (
                    <div className="border-t border-[#E5E7EB] p-5 bg-[#FAEEDA]/30">
                      <p className="text-xs uppercase tracking-[0.15em] font-sans font-semibold text-[#C17F24] mb-4">Visual</p>
                      <div
                        data-testid="insight-visual"
                        className="insight-visual-container"
                        dangerouslySetInnerHTML={{ __html: visualHtml }}
                      />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* History */}
        {promptHistory.length > 0 && (
          <div className="mt-6 space-y-3">
            <p className="text-xs uppercase tracking-[0.1em] font-sans font-semibold text-[#4B5563]">Previous questions</p>
            {promptHistory.map((h, i) => (
              <button key={i} onClick={() => { setCustomPrompt(h.prompt); setCustomAnswer({ text: h.answer, count: h.count, prompt: h.prompt }); }}
                className="block w-full text-left px-4 py-3 rounded-xl border border-[#E5E7EB] hover:bg-[#FAF9F6] transition-colors">
                <p className="text-sm font-sans font-medium text-[#1F2937] truncate">{h.prompt}</p>
                <p className="text-xs text-[#4B5563] mt-0.5 line-clamp-1">{h.answer.slice(0, 100)}...</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Manage Published Insights */}
      {publishedInsights.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 mt-8">
          <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">
            Published Insights ({publishedInsights.length})
          </h3>
          <div className="space-y-3">
            {publishedInsights.map(ins => (
              <div key={ins.id} className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#E5E7EB] hover:bg-[#FAF9F6]">
                <div className="min-w-0 flex-1 mr-4">
                  <p className="text-sm font-sans font-medium text-[#1F2937] truncate">{ins.title || ins.prompt}</p>
                  <p className="text-xs text-[#4B5563] mt-0.5">{new Date(ins.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} &middot; {ins.stories_analyzed} stories</p>
                </div>
                <button
                  data-testid={`delete-insight-${ins.id}`}
                  onClick={async () => {
                    if (window.confirm("Remove this insight from the public site?")) {
                      try {
                        await axios.delete(`${API}/admin/insights/${ins.id}`, { headers: { Authorization: `Bearer ${token}` } });
                        toast.success("Insight removed from site");
                        fetchPublished();
                      } catch { toast.error("Failed to delete"); }
                    }
                  }}
                  className="text-xs px-3 py-1.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 font-semibold flex-shrink-0"
                >
                  Unpublish
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Publish Title Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowPublishModal(false)}>
          <div data-testid="publish-title-modal" className="bg-white rounded-2xl border border-[#E5E7EB] w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-[#E5E7EB]">
              <h2 className="text-lg font-sans font-semibold text-[#1F2937]">Publish to site</h2>
              <p className="text-xs text-[#4B5563] mt-1">Set a clean title for the public insights page. This replaces the raw prompt.</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Title</Label>
                <Input
                  data-testid="publish-title-input"
                  value={publishTitle}
                  onChange={(e) => setPublishTitle(e.target.value)}
                  placeholder="e.g. The Emotional Heart of iambecause"
                  className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] h-11"
                />
              </div>
              <p className="text-xs text-[#4B5563]/60">Original prompt: <span className="italic">{customAnswer?.prompt?.slice(0, 100)}{customAnswer?.prompt?.length > 100 ? "..." : ""}</span></p>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E5E7EB]">
              <button onClick={() => setShowPublishModal(false)} className="btn-ghost !py-2.5 !px-6 text-sm">Cancel</button>
              <button
                data-testid="confirm-publish-btn"
                onClick={publishInsight}
                disabled={!publishTitle.trim()}
                className="btn-amber !py-2.5 !px-6 text-sm"
              >
                Publish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContentPanel({ token }) {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState("");
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/content/about`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setContent(res.data.content);
        setLastSaved(res.data.updated_at);
      })
      .catch(() => toast.error("Failed to load about content"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/content/about`, { content }, { headers: { Authorization: `Bearer ${token}` } });
      setLastSaved(res.data.updated_at);
      toast.success("About page updated!");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#C17F24]" /></div>;

  return (
    <div data-testid="content-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-sans font-semibold text-[#1F2937]">About Page</h3>
          <p className="text-xs text-[#4B5563] mt-1">
            Edit the content shown on the public About page. Uses Markdown formatting.
            {lastSaved && <span> &middot; Last saved {new Date(lastSaved).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="toggle-preview-btn"
            onClick={() => setPreview(!preview)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${preview ? "bg-[#C17F24] text-white" : "border border-[#E5E7EB] text-[#4B5563] hover:bg-[#FAF9F6]"}`}
          >
            {preview ? "Edit" : "Preview"}
          </button>
          <button
            data-testid="save-about-btn"
            onClick={handleSave}
            disabled={saving}
            className="btn-amber !py-2 !px-6 text-sm"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : "Save"}
          </button>
        </div>
      </div>

      {/* Formatting hints */}
      {!preview && (
        <div className="flex flex-wrap gap-2 text-xs text-[#4B5563]">
          <span className="px-2 py-1 rounded bg-[#FAF9F6] border border-[#E5E7EB]">**bold** = amber accent</span>
          <span className="px-2 py-1 rounded bg-[#FAF9F6] border border-[#E5E7EB]">*italic* = emphasis</span>
          <span className="px-2 py-1 rounded bg-[#FAF9F6] border border-[#E5E7EB]">&gt; quote = blockquote</span>
          <span className="px-2 py-1 rounded bg-[#FAF9F6] border border-[#E5E7EB]">--- = divider</span>
          <span className="px-2 py-1 rounded bg-[#FAF9F6] border border-[#E5E7EB]">## heading</span>
        </div>
      )}

      {preview ? (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-8 md:p-10 about-preview">
          <AboutPreview content={content} />
        </div>
      ) : (
        <Textarea
          data-testid="about-content-editor"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={24}
          className="rounded-xl border-[#E5E7EB] focus-visible:ring-[#C17F24] font-mono text-sm leading-relaxed"
          placeholder="Write the about page content in Markdown..."
        />
      )}

      {/* View live page link */}
      <div className="text-right">
        <a href="/about" target="_blank" rel="noreferrer" className="text-xs text-[#C17F24] font-semibold hover:underline">
          View live About page &rarr;
        </a>
      </div>
    </div>
  );
}

/* About content preview — same rendering as the public AboutPage */
function AboutPreview({ content }) {
  const ReactMarkdown = require("react-markdown").default;
  return (
    <ReactMarkdown
      components={{
        p: ({ children }) => {
          const text = typeof children === "string" ? children : "";
          if (text && text.includes("@") && text.includes(".") && text.trim().split(/\s+/).length <= 3) {
            return <p className="font-sans text-sm text-[#C17F24] font-semibold mt-8">{children}</p>;
          }
          return <p className="font-serif text-lg leading-loose text-[#1F2937] mb-6" style={{ lineHeight: '1.9' }}>{children}</p>;
        },
        strong: ({ children }) => <strong className="font-sans font-medium text-[#C17F24]">{children}</strong>,
        em: ({ children }) => <em className="text-[#4B5563] italic">{children}</em>,
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-[#C17F24]/30 pl-8 my-10">
            <div className="font-serif text-lg leading-loose text-[#4B5563]" style={{ lineHeight: '1.9' }}>{children}</div>
          </blockquote>
        ),
        hr: () => (
          <div className="my-10 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#E5E7EB]" />
            <div className="w-1.5 h-1.5 rounded-full bg-[#C17F24]" />
            <div className="flex-1 h-px bg-[#E5E7EB]" />
          </div>
        ),
        h1: ({ children }) => <h1 className="text-2xl font-sans font-medium text-[#1F2937] mt-10 mb-4">{children}</h1>,
        h2: ({ children }) => <h2 className="text-xl font-sans font-medium text-[#C17F24] mt-8 mb-3">{children}</h2>,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function PeopleTable({ stories, loading, peopleStatus, setPeopleStatus, peopleCountry, setPeopleCountry, dateFrom, setDateFrom, dateTo, setDateTo }) {
  const countries = [...new Set(stories.map(s => s.country).filter(Boolean))].sort();
  const [page, setPage] = useState(0);
  const perPage = 20;

  const filtered = stories.filter(s => {
    if (peopleStatus !== "all" && s.status !== peopleStatus) return false;
    if (peopleCountry !== "all" && s.country !== peopleCountry) return false;
    if (dateFrom && s.created_at < dateFrom) return false;
    if (dateTo && s.created_at < dateTo + "T23:59:59") return false;
    return true;
  });

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return d.slice(0, 10); }
  };

  const downloadCSV = () => {
    const headers = ["Name", "Role", "City", "Country", "Email", "WhatsApp", "Submission Date", "Status", "Media Type", "Anonymous"];
    const rows = filtered.map(s => [
      s.real_name || s.name, s.role, s.city || "", s.country || "", s.email || "", s.whatsapp || "",
      formatDate(s.created_at), s.status, s.media_type || "none", s.anonymous ? "yes" : "no"
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `iambecause-people-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#C17F24]" /></div>;

  return (
    <div data-testid="people-table-section">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Status</label>
          <select data-testid="people-filter-status" value={peopleStatus} onChange={e => setPeopleStatus(e.target.value)}
            className="rounded-xl border border-[#E5E7EB] px-3 h-9 text-sm focus:ring-2 focus:ring-[#C17F24] focus:outline-none bg-white">
            <option value="all">All</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="held">Held</option>
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">Country</label>
          <select data-testid="people-filter-country" value={peopleCountry} onChange={e => setPeopleCountry(e.target.value)}
            className="rounded-xl border border-[#E5E7EB] px-3 h-9 text-sm focus:ring-2 focus:ring-[#C17F24] focus:outline-none bg-white">
            <option value="all">All</option>
            {countries.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">From</label>
          <input type="date" data-testid="people-filter-from" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="rounded-xl border border-[#E5E7EB] px-3 h-9 text-sm focus:ring-2 focus:ring-[#C17F24] focus:outline-none bg-white" />
        </div>
        <div className="space-y-1">
          <label className="text-xs uppercase tracking-[0.12em] font-sans font-semibold text-[#4B5563]">To</label>
          <input type="date" data-testid="people-filter-to" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="rounded-xl border border-[#E5E7EB] px-3 h-9 text-sm focus:ring-2 focus:ring-[#C17F24] focus:outline-none bg-white" />
        </div>
        <button data-testid="download-csv-btn" onClick={downloadCSV}
          className="btn-amber !py-2 !px-5 text-sm ml-auto">
          <Download size={15} /> Download CSV
        </button>
      </div>

      <p className="text-xs text-[#4B5563] mb-3">{filtered.length} {filtered.length === 1 ? "person" : "people"}</p>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#FAF9F6]">
              {["Name", "Role", "City", "Country", "Email", "Date", "Status", "Media", "Anon"].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-[0.1em] font-sans font-semibold text-[#4B5563] whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-12 text-[#4B5563]">No people match filters.</td></tr>
            ) : filtered.slice(page * perPage, (page + 1) * perPage).map(s => (
              <tr key={s.id} className="border-b border-[#E5E7EB] last:border-0 hover:bg-[#FAF9F6]/50 transition-colors">
                <td className="px-4 py-3 font-sans font-medium text-[#1F2937] whitespace-nowrap">{s.real_name || s.name}</td>
                <td className="px-4 py-3 text-[#4B5563] max-w-[160px] truncate">{s.role}</td>
                <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">{s.city || "—"}</td>
                <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">{s.country || "—"}</td>
                <td className="px-4 py-3 text-[#4B5563] text-xs">{s.email || "—"}</td>
                <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap text-xs">{formatDate(s.created_at)}</td>
                <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                <td className="px-4 py-3 text-xs text-[#4B5563] capitalize">{s.media_type || "none"}</td>
                <td className="px-4 py-3 text-xs text-[#4B5563]">{s.anonymous ? "yes" : "no"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Pagination */}
      {filtered.length > perPage && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-[#4B5563]">
            Showing {page * perPage + 1}–{Math.min((page + 1) * perPage, filtered.length)} of {filtered.length}
          </p>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#E5E7EB] text-[#4B5563] hover:bg-[#FAF9F6] disabled:opacity-30">Previous</button>
            <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * perPage >= filtered.length}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#E5E7EB] text-[#4B5563] hover:bg-[#FAF9F6] disabled:opacity-30">Next</button>
          </div>
        </div>
      )}
    </div>
  );
}


function BackupButton({ token }) {
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runBackup = async () => {
    setRunning(true);
    setLastResult(null);
    try {
      const res = await axios.post(`${API}/admin/backup/run`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setLastResult(res.data);
      toast.success("Backup completed — files uploaded to Google Drive");
    } catch {
      setLastResult({ status: "error" });
      toast.error("Backup failed");
    }
    setRunning(false);
  };

  return (
    <div className="flex items-center gap-3">
      {lastResult && (
        <span className={`text-xs font-sans ${lastResult.status === "success" ? "text-green-600" : "text-red-500"}`}>
          {lastResult.status === "success" ? "Backed up" : "Failed"}
        </span>
      )}
      <button
        data-testid="backup-button"
        onClick={runBackup}
        disabled={running}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-sans font-semibold border transition-all hover:scale-105 disabled:opacity-50"
        style={{ borderColor: '#E5E7EB', color: '#4B5563', background: running ? '#FAF9F6' : 'white' }}
      >
        <Download size={14} />
        {running ? "Backing up..." : "Backup to Drive"}
      </button>
    </div>
  );
}

function MirrorPanel({ token }) {
  const [stats, setStats] = useState(null);
  const [mirrors, setMirrors] = useState([]);
  const [expandedMirror, setExpandedMirror] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [generatingTokens, setGeneratingTokens] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);

  const fetchStats = useCallback(() => {
    Promise.all([
      axios.get(`${API}/admin/mirror/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/admin/mirror/all`, { headers: { Authorization: `Bearer ${token}` } }),
    ]).then(([statsRes, mirrorsRes]) => {
      setStats(statsRes.data);
      setMirrors(mirrorsRes.data);
    }).catch(() => toast.error("Failed to load mirror data"))
    .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleGenerateAll = async () => {
    setGeneratingTokens(true);
    try {
      const res = await axios.post(`${API}/admin/mirror/generate-all-tokens`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Generated tokens for ${res.data.generated} stories`);
    } catch { toast.error("Failed to generate tokens"); }
    setGeneratingTokens(false);
  };

  const handleSendAllEmails = async () => {
    setSendingEmails(true);
    try {
      const res = await axios.post(`${API}/admin/mirror/send-all-emails`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Sent ${res.data.sent} emails. ${res.data.failed > 0 ? `${res.data.failed} failed.` : ""}`);
      fetchStats();
    } catch { toast.error("Failed to send emails"); }
    setSendingEmails(false);
  };

  const handleMarkSent = async (storyId) => {
    try {
      await axios.post(`${API}/admin/mirror/mark-sent/${storyId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Marked as sent");
      fetchStats();
    } catch { toast.error("Failed to mark as sent"); }
  };

  const buildWhatsAppMsg = (name, mirrorToken) => {
    const url = `https://iambecause.com/mirror/${mirrorToken}`;
    return `Hi ${name} — your story is live on iambecause. Thank you for sharing your why.\n\nI have something private for you — it's called The Mirror. A conversation just for you, with Mira. Not for the world. Just for your eyes.\n\nHere is your personal link: ${url}\n\nNo deadline. Come whenever you're ready. 🙏`;
  };

  const openWhatsApp = (name, mirrorToken, whatsappNumber) => {
    const msg = encodeURIComponent(buildWhatsAppMsg(name, mirrorToken));
    const digits = (whatsappNumber || "").replace(/\D/g, "");
    const base = digits.length >= 8 ? `https://wa.me/${digits}` : "https://wa.me/";
    window.open(`${base}?text=${msg}`, "_blank");
  };

  const copyMessage = (name, mirrorToken, storyId) => {
    navigator.clipboard.writeText(buildWhatsAppMsg(name, mirrorToken));
    setCopiedId(storyId);
    toast.success("Message copied");
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-[#C17F24]" /></div>;
  if (!stats) return null;

  const noEmailStories = stats.no_email_stories || [];
  const emailStories = stats.email_stories || [];

  return (
    <div data-testid="mirror-panel" className="space-y-8">
      {/* Top stats row */}
      <div className="flex flex-wrap gap-4">
        <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
          <p className="text-3xl font-sans font-semibold text-[#1F2937]">{stats.total}</p>
          <p className="text-xs uppercase tracking-[0.1em] font-sans text-[#4B5563] mt-1">Mirrors completed</p>
        </div>
        {stats.email_stats && (
          <>
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
              <p className="text-3xl font-sans font-semibold" style={{ color: '#C17F24' }}>{stats.email_stats.total}</p>
              <p className="text-xs uppercase tracking-[0.1em] font-sans text-[#4B5563] mt-1">Have email</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
              <p className="text-3xl font-sans font-semibold" style={{ color: '#E91E8C' }}>{noEmailStories.length}</p>
              <p className="text-xs uppercase tracking-[0.1em] font-sans text-[#4B5563] mt-1">No email — need outreach</p>
            </div>
          </>
        )}
      </div>

      {/* Tracking overview */}
      {stats.email_stats && stats.email_stats.total > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">Mirror delivery tracking</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-3 rounded-lg" style={{ background: '#FAF9F6' }}>
              <p className="text-lg font-sans font-semibold" style={{ color: '#4B5563' }}>{stats.email_stats.email_sent}</p>
              <p className="text-xs font-sans text-[#4B5563]">Email sent</p>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ background: '#FAF9F6' }}>
              <p className="text-lg font-sans font-semibold" style={{ color: '#C17F24' }}>{noEmailStories.filter(s => s.mirror_invite_sent).length}</p>
              <p className="text-xs font-sans text-[#4B5563]">WhatsApp sent</p>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ background: '#FAF9F6' }}>
              <p className="text-lg font-sans font-semibold" style={{ color: '#E91E8C' }}>{stats.email_stats.mirror_started}</p>
              <p className="text-xs font-sans text-[#4B5563]">Mirror started</p>
            </div>
            <div className="text-center p-3 rounded-lg" style={{ background: '#FAF9F6' }}>
              <p className="text-lg font-sans font-semibold text-green-600">{stats.email_stats.mirror_completed}</p>
              <p className="text-xs font-sans text-[#4B5563]">Mirror completed</p>
            </div>
          </div>
        </div>
      )}

      {stats.total > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {stats.words.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
              <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">Most common words</h3>
              <div className="flex flex-wrap gap-2">
                {stats.words.map(w => (
                  <span key={w.word} className="px-3 py-1.5 rounded-full text-sm font-sans" style={{ background: 'rgba(193,127,36,0.1)', color: '#C17F24' }}>
                    {w.word} <strong>({w.count})</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
          {Object.keys(stats.avg_ratings).length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
              <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-4">Average self-ratings (anonymous)</h3>
              <div className="space-y-2">
                {Object.entries(stats.avg_ratings).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-2 border-b border-[#E5E7EB] last:border-0">
                    <span className="text-sm font-sans capitalize text-[#4B5563]">{k}</span>
                    <span className="text-sm font-sans font-semibold text-[#C17F24]">{v}/10</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reach the ones without email */}
      {noEmailStories.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em]">Reach the ones without email</h3>
              <p className="text-xs font-sans text-[#4B5563] mt-1">{noEmailStories.length} approved stories with no email — Mirror pending</p>
            </div>
            <button
              data-testid="generate-all-tokens"
              onClick={handleGenerateAll}
              disabled={generatingTokens}
              className="px-4 py-2 rounded-lg text-xs font-sans font-semibold text-white transition-all hover:scale-105 disabled:opacity-50"
              style={{ background: '#C17F24' }}
            >
              {generatingTokens ? "Generating..." : "Generate all Mirror links"}
            </button>
          </div>
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {noEmailStories.map(s => (
              <div key={s.id} className="flex items-start gap-4 p-4 rounded-xl" style={{ background: '#FAF9F6', border: '0.5px solid #E5E7EB' }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-sans font-semibold text-[#1F2937]">{s.name}</p>
                    {s.mirror_invite_sent && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 font-sans">WhatsApp sent</span>
                    )}
                    {s.mirror_completed && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-[#C17F24]/10 text-[#C17F24] font-sans">Mirror completed</span>
                    )}
                    {!s.mirror_invite_sent && !s.mirror_completed && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${s.whatsapp ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>{s.whatsapp ? "WhatsApp invite ready" : "No contact — Mirror pending"}</span>
                    )}
                  </div>
                  <p className="text-xs font-sans text-[#4B5563]">{s.role}{s.city ? ` · ${s.city}` : ""}</p>
                  <p className="text-xs font-serif italic text-[#4B5563] mt-1 line-clamp-1">{s.why_snippet}</p>
                </div>
                <div className="flex flex-col gap-2 flex-shrink-0">
                  <button
                    data-testid={`whatsapp-${s.id}`}
                    onClick={() => { openWhatsApp(s.name, s.mirror_token, s.whatsapp); handleMarkSent(s.id); }}
                    className="px-3 py-1.5 rounded-lg text-xs font-sans font-semibold text-white transition-all hover:scale-105"
                    style={{ background: '#25D366' }}
                  >
                    WhatsApp
                  </button>
                  <button
                    data-testid={`copy-msg-${s.id}`}
                    onClick={() => copyMessage(s.name, s.mirror_token, s.id)}
                    className="px-3 py-1.5 rounded-lg text-xs font-sans font-semibold border transition-all hover:scale-105"
                    style={{ borderColor: '#E5E7EB', color: '#4B5563' }}
                  >
                    {copiedId === s.id ? "Copied!" : "Copy message"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* People with email — individual tracking */}
      {emailStories.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em]">People with email</h3>
              <p className="text-xs font-sans text-[#4B5563] mt-1">{emailStories.length} people — individual Mirror tracking</p>
            </div>
            <button
              data-testid="send-all-emails"
              onClick={handleSendAllEmails}
              disabled={sendingEmails || emailStories.every(s => s.email_sent)}
              className="px-4 py-2 rounded-lg text-xs font-sans font-semibold text-white transition-all hover:scale-105 disabled:opacity-50"
              style={{ background: '#C17F24' }}
            >
              {sendingEmails ? "Sending..." : emailStories.every(s => s.email_sent) ? "All emails sent" : "Send all Mirror emails"}
            </button>
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {emailStories.map(s => (
              <div key={s.id} className="flex items-center gap-4 p-3.5 rounded-xl" style={{ background: '#FAF9F6', border: '0.5px solid #E5E7EB' }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-sans font-semibold text-[#1F2937]">{s.name}</p>
                    <span className="text-xs font-sans text-[#4B5563]/50">{s.email}</span>
                  </div>
                  <p className="text-xs font-sans text-[#4B5563] mt-0.5">{s.role}{s.city ? ` · ${s.city}` : ""}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  {s.email_sent ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 font-sans font-semibold">Email sent</span>
                  ) : s.email_failed ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-500 font-sans font-semibold">Email failed</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-600 font-sans">Email pending</span>
                  )}
                  {s.mirror_completed ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#C17F24]/10 text-[#C17F24] font-sans font-semibold">Mirror done</span>
                  ) : s.mirror_started ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-sans">Mirror started</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-400 font-sans">Waiting</span>
                  )}
                  <button
                    onClick={() => { openWhatsApp(s.name, s.mirror_token, s.whatsapp); handleMarkSent(s.id); }}
                    className="px-2.5 py-1 rounded-lg text-xs font-sans font-semibold text-white transition-all hover:scale-105"
                    style={{ background: '#25D366' }}
                  >
                    WhatsApp
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/mirror/${s.mirror_token}`); toast.success(`Mirror link for ${s.name} copied`); }}
                    className="text-xs font-sans text-[#C17F24] hover:underline"
                  >
                    Copy link
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Mirrors — Full Individual View */}
      {mirrors.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6">
          <h3 className="text-sm font-sans font-semibold text-[#1F2937] uppercase tracking-[0.1em] mb-1">Completed Mirrors</h3>
          <p className="text-xs font-sans text-[#4B5563] mb-5">{mirrors.length} people have completed their Mirror</p>
          <div className="space-y-3">
            {mirrors.map(m => {
              const isExpanded = expandedMirror === m.story_id;
              const avg = m.self_ratings?.average || 0;
              const word = m.road_ahead?.one_word || "";
              return (
                <div key={m.story_id} className="rounded-xl overflow-hidden" style={{ border: '0.5px solid #E5E7EB' }}>
                  {/* Header — always visible */}
                  <button
                    onClick={() => setExpandedMirror(isExpanded ? null : m.story_id)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-[#FAF9F6] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-sans font-semibold text-[#1F2937]">{m.person_name}</p>
                      <span className="text-xs font-sans" style={{ color: '#4B5563' }}>{m.fy}</span>
                      {word && <span className="px-2.5 py-0.5 rounded-full text-xs font-sans font-semibold" style={{ background: 'rgba(193,127,36,0.1)', color: '#C17F24' }}>{word}</span>}
                      {avg > 0 && <span className="text-xs font-sans text-[#4B5563]">Avg: {avg}/10</span>}
                    </div>
                    <span className="text-xs font-sans text-[#4B5563]">{isExpanded ? "▲ Close" : "▼ View"}</span>
                  </button>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-5 pb-5 space-y-5" style={{ borderTop: '0.5px solid #E5E7EB' }}>
                      {/* Rearview */}
                      <div className="pt-4">
                        <p className="text-xs uppercase tracking-[0.1em] font-sans font-semibold mb-2" style={{ color: '#C17F24' }}>The Rearview</p>
                        {m.rearview?.excellence && (
                          <div className="mb-3 pl-3" style={{ borderLeft: '2px solid #C17F24' }}>
                            <p className="text-[10px] uppercase tracking-[0.1em] font-sans font-semibold mb-1 text-[#4B5563]">Where I excelled</p>
                            <p className="text-sm font-serif text-[#1F2937] leading-relaxed">{m.rearview.excellence}</p>
                          </div>
                        )}
                        {m.rearview?.near_misses && (
                          <div className="mb-3 pl-3" style={{ borderLeft: '2px solid #E5E7EB' }}>
                            <p className="text-[10px] uppercase tracking-[0.1em] font-sans font-semibold mb-1 text-[#4B5563]">What didn't quite make it</p>
                            <p className="text-sm font-serif text-[#1F2937] leading-relaxed">{m.rearview.near_misses}</p>
                          </div>
                        )}
                        {m.rearview?.failures && (
                          <div className="mb-3 pl-3" style={{ borderLeft: '2px solid #DC2626' }}>
                            <p className="text-[10px] uppercase tracking-[0.1em] font-sans font-semibold mb-1 text-[#4B5563]">What failed</p>
                            <p className="text-sm font-serif text-[#1F2937] leading-relaxed italic">{m.rearview.failures}</p>
                          </div>
                        )}
                      </div>

                      {/* Self ratings */}
                      <div>
                        <p className="text-xs uppercase tracking-[0.1em] font-sans font-semibold mb-2" style={{ color: '#C17F24' }}>Self-ratings</p>
                        <div className="flex flex-wrap gap-3">
                          {["impact", "innovation", "ownership", "collaboration", "growth", "reliability"].map(dim => (
                            <div key={dim} className="text-center px-3 py-2 rounded-lg" style={{ background: '#FAF9F6', minWidth: '70px' }}>
                              <p className="text-lg font-sans font-semibold" style={{ color: (m.self_ratings?.[dim] || 0) >= 7 ? '#C17F24' : '#4B5563' }}>{m.self_ratings?.[dim] || "—"}</p>
                              <p className="text-[10px] uppercase tracking-[0.05em] font-sans text-[#4B5563]">{dim}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Road ahead */}
                      <div>
                        <p className="text-xs uppercase tracking-[0.1em] font-sans font-semibold mb-2" style={{ color: '#C17F24' }}>Road ahead</p>
                        {m.road_ahead?.big_bets && (
                          <div className="mb-2 pl-3" style={{ borderLeft: '2px solid #C17F24' }}>
                            <p className="text-[10px] uppercase tracking-[0.1em] font-sans font-semibold mb-1 text-[#4B5563]">Big bets</p>
                            <p className="text-sm font-serif text-[#1F2937] leading-relaxed">{m.road_ahead.big_bets}</p>
                          </div>
                        )}
                        {m.road_ahead?.needs && (
                          <div className="pl-3" style={{ borderLeft: '2px solid #E5E7EB' }}>
                            <p className="text-[10px] uppercase tracking-[0.1em] font-sans font-semibold mb-1 text-[#4B5563]">What I need</p>
                            <p className="text-sm font-serif text-[#1F2937] leading-relaxed">{m.road_ahead.needs}</p>
                          </div>
                        )}
                      </div>

                      {/* Mira's reflections */}
                      {m.mira_reflection && (
                        <div className="rounded-xl p-4" style={{ background: '#1F2937' }}>
                          <p className="text-xs uppercase tracking-[0.1em] font-sans font-semibold mb-3" style={{ color: '#E91E8C' }}>What Mira noticed</p>
                          {m.mira_reflection.mirror_reflection && (
                            <p className="text-sm font-serif italic leading-relaxed mb-3" style={{ color: 'rgba(255,255,255,0.8)' }}>{m.mira_reflection.mirror_reflection}</p>
                          )}
                          {m.mira_reflection.mirror_theme && (
                            <p className="text-sm font-serif italic mb-2" style={{ color: '#C17F24' }}>Theme: {m.mira_reflection.mirror_theme}</p>
                          )}
                          {m.mira_reflection.word_insight && (
                            <p className="text-xs font-serif italic" style={{ color: 'rgba(255,255,255,0.6)' }}>Word — {word}: {m.mira_reflection.word_insight}</p>
                          )}
                        </div>
                      )}

                      {/* Download */}
                      <div className="flex items-center gap-3 pt-2">
                        <a
                          href={`${window.location.origin}/api/mirror/document/${m.mirror_token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-sans font-semibold px-3 py-1.5 rounded-lg text-white transition-all hover:scale-105"
                          style={{ background: '#C17F24', textDecoration: 'none' }}
                        >
                          Download .docx
                        </a>
                        <span className="text-xs font-sans text-[#4B5563]">
                          Completed {m.completed_at ? new Date(m.completed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <p className="text-xs font-sans italic" style={{ color: '#888780' }}>
        Visible only to the founder. Never shared externally.
      </p>
    </div>
  );
}

function IconsPanel({ token }) {
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingIcon, setEditingIcon] = useState(null);
  const [addingEpisode, setAddingEpisode] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchIcons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/icons`, { headers });
      setIcons(res.data);
    } catch {
      toast.error("Failed to load icons");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchIcons(); }, [fetchIcons]);

  const saveIcon = async (iconId, updates) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/icons/${iconId}`, updates, { headers });
      toast.success("Icon updated");
      await fetchIcons();
      setEditingIcon(null);
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addEpisode = async (iconId, episode) => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/icons/${iconId}/episodes`, episode, { headers });
      toast.success("Episode added");
      await fetchIcons();
      setAddingEpisode(null);
    } catch {
      toast.error("Failed to add episode");
    } finally {
      setSaving(false);
    }
  };

  const removeEpisode = async (iconId, idx) => {
    if (!window.confirm("Remove this episode?")) return;
    try {
      await axios.delete(`${API}/admin/icons/${iconId}/episodes/${idx}`, { headers });
      toast.success("Episode removed");
      await fetchIcons();
    } catch {
      toast.error("Failed to remove episode");
    }
  };

  if (loading) return <div className="text-center py-12 text-[#4B5563]">Loading icons...</div>;

  return (
    <div data-testid="admin-icons-panel" className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-[#1F2937]">Icons of India ({icons.length})</h2>
        <a href="/icons" target="_blank" rel="noreferrer" className="text-xs text-[#D4621A] hover:underline">View live page &rarr;</a>
      </div>

      {icons.map(icon => (
        <div key={icon.id} className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
          <div className="p-4 flex items-center gap-4">
            {/* Portrait */}
            {icon.portrait_url ? (
              <img src={icon.portrait_url} alt={icon.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-[#F2E8DD] flex items-center justify-center text-sm font-semibold flex-shrink-0" style={{ color: '#D4621A' }}>
                {icon.name.split(" ").map(w => w[0]).join("").slice(0, 2)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-[#1F2937] truncate">{icon.name}</h3>
                {icon.is_anchor && <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4621A] text-white">Anchor</span>}
                {icon.featured && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Featured</span>}
              </div>
              <p className="text-xs text-[#6B6B63] truncate">{icon.title_line}</p>
              <div className="flex gap-1 mt-1">
                {(icon.category || []).map(c => (
                  <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-[#F2E8DD] text-[#D4621A]">{c}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-[#6B6B63]">{(icon.episodes || []).length} ep</span>
              <button
                data-testid={`edit-icon-${icon.slug}`}
                onClick={() => setEditingIcon(editingIcon === icon.id ? null : icon.id)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#E5E7EB] text-[#4B5563] hover:bg-[#FAF9F6] transition-colors"
              >
                <Pencil size={12} className="inline mr-1" /> Edit
              </button>
              <button
                data-testid={`add-episode-${icon.slug}`}
                onClick={() => setAddingEpisode(addingEpisode === icon.id ? null : icon.id)}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#D4621A]/30 text-[#D4621A] hover:bg-[#F2E8DD] transition-colors"
              >
                + Episode
              </button>
            </div>
          </div>

          {/* Edit form */}
          {editingIcon === icon.id && (
            <IconEditForm icon={icon} onSave={(updates) => saveIcon(icon.id, updates)} saving={saving} onCancel={() => setEditingIcon(null)} />
          )}

          {/* Add episode form */}
          {addingEpisode === icon.id && (
            <EpisodeAddForm onAdd={(ep) => addEpisode(icon.id, ep)} saving={saving} onCancel={() => setAddingEpisode(null)} />
          )}

          {/* Existing episodes */}
          {(icon.episodes || []).length > 0 && editingIcon === icon.id && (
            <div className="px-4 pb-4">
              <p className="text-xs font-semibold text-[#4B5563] mb-2">Episodes:</p>
              {icon.episodes.map((ep, idx) => (
                <div key={idx} className="flex items-center gap-3 py-2 border-t border-[#E5E7EB]">
                  <span className="text-xs font-bold text-[#D4621A] w-6">#{ep.episode_number || idx + 1}</span>
                  <span className="text-xs text-[#1F2937] flex-1 truncate">{ep.title}</span>
                  {ep.youtube_url && <span className="text-[10px] text-[#6B6B63]">YT</span>}
                  <button onClick={() => removeEpisode(icon.id, idx)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function IconEditForm({ icon, onSave, saving, onCancel }) {
  const [form, setForm] = useState({
    name: icon.name || "",
    title_line: icon.title_line || "",
    bio_short: icon.bio_short || "",
    bio_full: icon.bio_full || "",
    portrait_url: icon.portrait_url || "",
    i_am_because: icon.i_am_because || "",
    website: icon.website || "",
    featured: icon.featured || false,
    category: (icon.category || []).join(", "),
  });
  return (
    <div className="px-4 pb-4 border-t border-[#E5E7EB] pt-4 space-y-3 bg-[#FAF9F6]">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-[#4B5563]">Name</Label>
          <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-[#4B5563]">Title Line</Label>
          <Input value={form.title_line} onChange={e => setForm({...form, title_line: e.target.value})} className="h-9 text-sm" />
        </div>
      </div>
      <div>
        <Label className="text-xs text-[#4B5563]">Short Bio</Label>
        <Textarea value={form.bio_short} onChange={e => setForm({...form, bio_short: e.target.value})} rows={2} className="text-sm" />
      </div>
      <div>
        <Label className="text-xs text-[#4B5563]">Full Bio</Label>
        <Textarea value={form.bio_full} onChange={e => setForm({...form, bio_full: e.target.value})} rows={4} className="text-sm" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-[#4B5563]">Portrait URL</Label>
          <Input value={form.portrait_url} onChange={e => setForm({...form, portrait_url: e.target.value})} className="h-9 text-sm" placeholder="https://..." />
        </div>
        <div>
          <Label className="text-xs text-[#4B5563]">Website</Label>
          <Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} className="h-9 text-sm" placeholder="https://..." />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-[#4B5563]">Categories (comma-separated)</Label>
          <Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="h-9 text-sm" placeholder="Technology, Science" />
        </div>
        <div>
          <Label className="text-xs text-[#4B5563]">"I am because..." quote</Label>
          <Input value={form.i_am_because} onChange={e => setForm({...form, i_am_because: e.target.value})} className="h-9 text-sm" placeholder="Their why..." />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} id="icon-featured" />
        <label htmlFor="icon-featured" className="text-xs text-[#4B5563]">Featured</label>
      </div>
      <div className="flex gap-2 pt-2">
        <button
          onClick={() => onSave({
            ...form,
            category: form.category.split(",").map(s => s.trim()).filter(Boolean),
          })}
          disabled={saving}
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#D4621A] text-white hover:bg-[#B5520F] transition-colors disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#E5E7EB] text-[#4B5563] hover:bg-white transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}

function EpisodeAddForm({ onAdd, saving, onCancel }) {
  const [form, setForm] = useState({ title: "", youtube_url: "", description: "", duration: "", episode_number: 1 });
  return (
    <div className="px-4 pb-4 border-t border-[#E5E7EB] pt-4 space-y-3 bg-[#FFF8F0]">
      <p className="text-xs font-semibold text-[#D4621A]">Add Episode</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-[#4B5563]">Title</Label>
          <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="h-9 text-sm" placeholder="Episode title" />
        </div>
        <div>
          <Label className="text-xs text-[#4B5563]">YouTube URL</Label>
          <Input value={form.youtube_url} onChange={e => setForm({...form, youtube_url: e.target.value})} className="h-9 text-sm" placeholder="https://youtube.com/watch?v=..." />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="text-xs text-[#4B5563]">Episode #</Label>
          <Input type="number" value={form.episode_number} onChange={e => setForm({...form, episode_number: parseInt(e.target.value) || 1})} className="h-9 text-sm" />
        </div>
        <div>
          <Label className="text-xs text-[#4B5563]">Duration</Label>
          <Input value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} className="h-9 text-sm" placeholder="45 min" />
        </div>
        <div>
          <Label className="text-xs text-[#4B5563]">Description</Label>
          <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="h-9 text-sm" placeholder="Brief description" />
        </div>
      </div>
      <div className="flex gap-2 pt-2">
        <button
          onClick={() => onAdd(form)}
          disabled={saving || !form.title}
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#D4621A] text-white hover:bg-[#B5520F] transition-colors disabled:opacity-50"
        >
          {saving ? "Adding..." : "Add Episode"}
        </button>
        <button onClick={onCancel} className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#E5E7EB] text-[#4B5563] hover:bg-white transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
}



function AdminDashboard({ token, onLogout }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [updating, setUpdating] = useState(null);
  const [editingStory, setEditingStory] = useState(null);
  const [creatingStory, setCreatingStory] = useState(false);
  const [activeTab, setActiveTab] = useState("stories");
  const [storiesPage, setStoriesPage] = useState(0);
  const storiesPerPage = 15;
  // People filters
  const [peopleStatus, setPeopleStatus] = useState("all");
  const [peopleCountry, setPeopleCountry] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stories`, { headers });
      setStories(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
        return;
      }
      toast.error("Failed to load stories");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStories();
  }, [fetchStories]);

  const updateStory = async (id, update) => {
    setUpdating(id);
    try {
      await axios.patch(`${API}/admin/stories/${id}`, update, { headers });
      await fetchStories();
      toast.success("Story updated");
    } catch (err) {
      toast.error("Failed to update story");
    } finally {
      setUpdating(null);
    }
  };

  const handleEditSaved = () => {
    setEditingStory(null);
    fetchStories();
  };

  const filteredStories = filter === "all"
    ? stories
    : stories.filter((s) => s.status === filter);

  const counts = {
    all: stories.length,
    pending: stories.filter((s) => s.status === "pending").length,
    approved: stories.filter((s) => s.status === "approved").length,
    held: stories.filter((s) => s.status === "held").length,
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FAF9F6]">
      <Toaster position="top-center" richColors />

      {/* Edit Modal */}
      {editingStory && (
        <EditModal
          story={editingStory}
          token={token}
          onClose={() => setEditingStory(null)}
          onSaved={handleEditSaved}
        />
      )}

      {/* Create Modal */}
      {creatingStory && (
        <CreateModal
          token={token}
          onClose={() => setCreatingStory(false)}
          onCreated={() => { setCreatingStory(false); fetchStories(); }}
        />
      )}

      {/* Admin Header */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-baseline gap-0 text-xl font-medium tracking-tight select-none">
            <span className="text-[#1F2937]">iam</span>
            <span className="text-[#C17F24]">because</span>
            <span className="text-xs font-normal text-[#4B5563] ml-2">admin</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-create-btn"
              onClick={() => setCreatingStory(true)}
              className="btn-amber !py-2 !px-5 text-sm"
            >
              + New Story
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#1F2937] transition-colors"
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {Object.entries(counts).map(([key, count]) => (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => setFilter(key)}
              className={`rounded-xl border p-4 text-left transition-all ${
                filter === key
                  ? "border-[#C17F24] bg-[#C17F24]/5"
                  : "border-[#E5E7EB] bg-white hover:border-[#C17F24]/30"
              }`}
            >
              <p className="text-2xl font-sans font-semibold text-[#1F2937]">{count}</p>
              <p className="text-xs uppercase tracking-[0.15em] font-sans text-[#4B5563] mt-1 capitalize">{key}</p>
            </button>
          ))}
        </div>

        {/* Tabs: Stories / People / Insights / Content / Mirror */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-1 bg-white rounded-xl border border-[#E5E7EB] p-1 w-fit">
          <button data-testid="tab-stories" onClick={() => setActiveTab("stories")}
            className={`px-5 py-2 rounded-lg text-sm font-sans font-semibold transition-all ${activeTab === "stories" ? "bg-[#C17F24] text-white" : "text-[#4B5563] hover:bg-[#FAF9F6]"}`}>
            Stories
          </button>
          <button data-testid="tab-people" onClick={() => setActiveTab("people")}
            className={`px-5 py-2 rounded-lg text-sm font-sans font-semibold transition-all ${activeTab === "people" ? "bg-[#C17F24] text-white" : "text-[#4B5563] hover:bg-[#FAF9F6]"}`}>
            People
          </button>
          <button data-testid="tab-insights" onClick={() => setActiveTab("insights")}
            className={`px-5 py-2 rounded-lg text-sm font-sans font-semibold transition-all ${activeTab === "insights" ? "bg-[#C17F24] text-white" : "text-[#4B5563] hover:bg-[#FAF9F6]"}`}>
            Insights
          </button>
          <button data-testid="tab-content" onClick={() => setActiveTab("content")}
            className={`px-5 py-2 rounded-lg text-sm font-sans font-semibold transition-all ${activeTab === "content" ? "bg-[#C17F24] text-white" : "text-[#4B5563] hover:bg-[#FAF9F6]"}`}>
            Content
          </button>
          <button data-testid="tab-mirror" onClick={() => setActiveTab("mirror")}
            className={`px-5 py-2 rounded-lg text-sm font-sans font-semibold transition-all ${activeTab === "mirror" ? "bg-[#C17F24] text-white" : "text-[#4B5563] hover:bg-[#FAF9F6]"}`}>
            Mirror
          </button>
          <button data-testid="tab-icons" onClick={() => setActiveTab("icons")}
            className={`px-5 py-2 rounded-lg text-sm font-sans font-semibold transition-all ${activeTab === "icons" ? "bg-[#D4621A] text-white" : "text-[#4B5563] hover:bg-[#FAF9F6]"}`}>
            Icons
          </button>
          </div>
          <BackupButton token={token} />
        </div>

        {activeTab === "mirror" ? (
          <MirrorPanel token={token} />
        ) : activeTab === "icons" ? (
          <IconsPanel token={token} />
        ) : activeTab === "insights" ? (
          <InsightsPanel token={token} />
        ) : activeTab === "content" ? (
          <ContentPanel token={token} />
        ) : activeTab === "people" ? (
          <PeopleTable
            stories={stories}
            loading={loading}
            peopleStatus={peopleStatus}
            setPeopleStatus={setPeopleStatus}
            peopleCountry={peopleCountry}
            setPeopleCountry={setPeopleCountry}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
          />
        ) : (
        <>
        {/* Stories List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={24} className="animate-spin text-[#C17F24]" />
          </div>
        ) : filteredStories.length === 0 ? (
          <div data-testid="no-admin-stories" className="text-center py-20">
            <p className="text-[#4B5563]">No stories in this category.</p>
          </div>
        ) : (
          <>
          <div className="space-y-4">
            {filteredStories.slice(storiesPage * storiesPerPage, (storiesPage + 1) * storiesPerPage).map((story) => (
              <div
                key={story.id}
                data-testid={`admin-story-${story.id}`}
                className="bg-white rounded-2xl border border-[#E5E7EB] p-5 md:p-6 flex flex-col md:flex-row gap-4 md:gap-6"
              >
                {/* Thumbnail */}
                <div className="w-full md:w-24 h-24 md:h-24 rounded-xl overflow-hidden flex-shrink-0 bg-[#FAF9F6]">
                  {story.media_url && story.media_type === "image" ? (
                    <img src={story.media_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-serif text-2xl text-[#C17F24]/30">{story.name?.charAt(0)}</span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-sans font-semibold text-[#1F2937]">{story.name}</h3>
                        <StatusBadge status={story.status} />
                        {story.anonymous && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200">Anonymous</span>
                        )}
                        {story.featured && (
                          <span className="inline-flex items-center gap-1 text-xs text-[#C17F24] font-semibold">
                            <Star size={12} className="fill-[#C17F24]" /> Featured
                          </span>
                        )}
                        {story.source === "mira" && (
                          <span data-testid="mira-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border" style={{ borderColor: 'rgba(233,30,140,0.3)', color: '#E91E8C', background: 'rgba(233,30,140,0.08)' }}>
                            {story.was_voice ? (
                              <><Mic size={10} /> Shared by voice</>
                            ) : story.was_mixed ? (
                              <><Mic size={10} /> Voice + text</>
                            ) : (
                              <>via Mira</>
                            )}
                          </span>
                        )}
                        {story.stage === "seeking" && (
                          <span data-testid="seeking-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border" style={{ borderColor: 'rgba(193,127,36,0.3)', color: '#C17F24', background: 'rgba(193,127,36,0.08)' }}>
                            <Compass size={10} /> Seeking
                          </span>
                        )}
                        {!story.email && (
                          <span data-testid="no-email-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border" style={{ borderColor: 'rgba(220,38,38,0.3)', color: '#DC2626', background: 'rgba(220,38,38,0.05)' }}>
                            No email — Mirror pending
                          </span>
                        )}
                        {story.moderation_score != null && (
                          <ModerationBadge score={story.moderation_score} flags={story.moderation_flags} summary={story.moderation_summary} />
                        )}
                      </div>
                      <p className="text-sm text-[#4B5563] mt-0.5">{story.role}</p>
                      {story.email && (
                        <p className="text-xs text-[#4B5563]/60 mt-0.5">{story.email}</p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm font-serif text-[#4B5563] line-clamp-2 mb-3">
                    {story.why}
                  </p>
                  {story.mira_reflection && (
                    <div className="mb-3 rounded-lg p-2.5" style={{ background: 'rgba(233,30,140,0.04)', border: '0.5px solid rgba(233,30,140,0.15)' }}>
                      <p className="text-xs font-sans font-semibold mb-1" style={{ color: '#E91E8C' }}>Mira's reflection</p>
                      <p className="text-xs font-serif italic" style={{ color: '#4B5563' }}>{story.mira_reflection}</p>
                    </div>
                  )}

                  {/* Mirror status */}
                  {story.status === "approved" && story.mirror_token && (
                    <div data-testid={`mirror-status-${story.id}`} className="mb-3 flex items-center gap-2 flex-wrap">
                      <a
                        href={`/mirror/${story.mirror_token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-sans font-semibold border transition-all hover:scale-105"
                        style={{ borderColor: 'rgba(193,127,36,0.3)', color: '#C17F24', background: 'rgba(193,127,36,0.06)' }}
                      >
                        <Compass size={10} /> Mirror link
                      </a>
                      <button
                        onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/mirror/${story.mirror_token}`); toast.success("Mirror link copied"); }}
                        className="text-xs font-sans text-[#4B5563]/60 hover:text-[#4B5563] transition-colors"
                      >
                        Copy link
                      </button>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {/* Edit button */}
                    <button
                      data-testid={`edit-${story.id}`}
                      onClick={() => setEditingStory(story)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#C17F24]/10 text-[#C17F24] border border-[#C17F24]/20 hover:bg-[#C17F24]/20 transition-colors"
                    >
                      <Pencil size={14} /> Edit
                    </button>
                    {story.status !== "approved" && (
                      <button
                        data-testid={`approve-${story.id}`}
                        onClick={() => updateStory(story.id, { status: "approved" })}
                        disabled={updating === story.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                      >
                        <Check size={14} /> Approve
                      </button>
                    )}
                    {story.status !== "held" && (
                      <button
                        data-testid={`hold-${story.id}`}
                        onClick={() => updateStory(story.id, { status: "held" })}
                        disabled={updating === story.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 transition-colors"
                      >
                        <Pause size={14} /> Hold
                      </button>
                    )}
                    {!story.featured && story.status === "approved" && (
                      <button
                        data-testid={`feature-${story.id}`}
                        onClick={() => updateStory(story.id, { featured: true })}
                        disabled={updating === story.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                      >
                        <Star size={14} /> Feature
                      </button>
                    )}
                    {story.featured && (
                      <button
                        data-testid={`unfeature-${story.id}`}
                        onClick={() => updateStory(story.id, { featured: false })}
                        disabled={updating === story.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                      >
                        <Star size={14} /> Unfeature
                      </button>
                    )}
                    <a
                      href={`/story/${story.id}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`view-${story.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                    >
                      <Eye size={14} /> View
                    </a>
                    {story.status === "approved" && (
                      <InstagramCard story={story} />
                    )}
                    <button
                      data-testid={`delete-${story.id}`}
                      onClick={() => {
                        if (window.confirm(`Delete "${story.name}"'s story? This cannot be undone.`)) {
                          axios.delete(`${API}/admin/stories/${story.id}`, { headers })
                            .then(() => { toast.success("Story deleted"); fetchStories(); })
                            .catch(() => toast.error("Failed to delete"));
                        }
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors"
                    >
                      <X size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Stories pagination */}
          {filteredStories.length > storiesPerPage && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-xs text-[#4B5563]">
                Showing {storiesPage * storiesPerPage + 1}–{Math.min((storiesPage + 1) * storiesPerPage, filteredStories.length)} of {filteredStories.length}
              </p>
              <div className="flex gap-2">
                <button onClick={() => setStoriesPage(p => Math.max(0, p - 1))} disabled={storiesPage === 0}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#E5E7EB] text-[#4B5563] hover:bg-[#FAF9F6] disabled:opacity-30">Previous</button>
                <button onClick={() => setStoriesPage(p => p + 1)} disabled={(storiesPage + 1) * storiesPerPage >= filteredStories.length}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#E5E7EB] text-[#4B5563] hover:bg-[#FAF9F6] disabled:opacity-30">Next</button>
              </div>
            </div>
          )}
          </>
        )}
        </>
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("admin_token"));
  const navigate = useNavigate();

  const handleLogin = (t) => {
    localStorage.setItem("admin_token", t);
    setToken(t);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(null);
  };

  if (!token) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard token={token} onLogout={handleLogout} />;
}
